import { Component, Inject, NgZone, OnInit } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { ApiService } from "src/app/services/api.service";
import { apiErrorMessage, formatInZone, VISIT_TIME_ZONES } from "../visit-time";

export interface VisitDetailResult {
  action: "edit" | "changed";
  visit?: any;
}

@Component({
  selector: "app-visit-detail",
  templateUrl: "./visit-detail.component.html",
  styleUrls: ["./visit-detail.component.scss"],
  standalone: false,
})
export class VisitDetailComponent implements OnInit {
  visit: any = null;
  loading = true;
  busy = false;
  confirmingCancel = false;
  errorMessage = "";
  infoMessage = "";
  copied = false;
  transcript: { speakerName: string; text: string }[] | null = null;
  transcriptLoading = false;
  private changed = false;

  constructor(
    private apiService: ApiService,
    private ngZone: NgZone,
    private dialogRef: MatDialogRef<VisitDetailComponent, VisitDetailResult>,
    @Inject(MAT_DIALOG_DATA) public data: { visitKey: number }
  ) {}

  ngOnInit(): void {
    this.apiService.getVisit(this.data.visitKey).subscribe({
      next: (res) => {
        this.visit = res;
        this.loading = false;
      },
      error: (err) => {
        this.loading = false;
        this.errorMessage = apiErrorMessage(err, "The visit could not be loaded.");
      },
    });
  }

  get isVideo(): boolean {
    return this.visit?.visitType === "VIDEO";
  }

  get isCancelled(): boolean {
    return this.visit?.status === "cancelled";
  }

  get isPast(): boolean {
    return !!this.visit && new Date(this.visit.endUtc).getTime() < Date.now();
  }

  /** Header chip: Cancelled, Ended or Upcoming. */
  get statusChip(): { label: string; tone: "danger" | "neutral" | "info" } {
    if (this.isCancelled) return { label: "Cancelled", tone: "danger" };
    if (this.isPast) return { label: "Ended", tone: "neutral" };
    return { label: "Upcoming", tone: "info" };
  }

  private get zone(): string {
    return this.visit?.timeZone || "UTC";
  }

  private get range(): { start: Date; end: Date } | null {
    if (!this.visit) return null;
    const start = new Date(this.visit.startUtc);
    const end = new Date(this.visit.endUtc);
    return Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) ? null : { start, end };
  }

  /** "Tuesday, October 13, 2026" in the visit's own time zone. */
  get dayLabel(): string {
    const r = this.range;
    return r ? formatInZone(r.start, this.zone, { weekday: "long", month: "long", day: "numeric", year: "numeric" }) : "";
  }

  /** "7:00 PM – 8:00 PM". */
  get timeLabel(): string {
    const r = this.range;
    if (!r) return "";
    const time: Intl.DateTimeFormatOptions = { hour: "numeric", minute: "2-digit" };
    return `${formatInZone(r.start, this.zone, time)} – ${formatInZone(r.end, this.zone, time)}`;
  }

  get zoneLabel(): string {
    return VISIT_TIME_ZONES.find((z) => z.id === this.zone)?.label || this.zone;
  }

  /** "30 min", "1 hr", "1 hr 15 min". */
  get durationLabel(): string {
    const r = this.range;
    if (!r) return "";
    const minutes = Math.round((r.end.getTime() - r.start.getTime()) / 60000);
    if (minutes <= 0) return "";
    const hours = Math.floor(minutes / 60);
    const rest = minutes % 60;
    if (!hours) return `${rest} min`;
    return rest ? `${hours} hr ${rest} min` : `${hours} hr`;
  }

  initialsOf(attendee: { displayName?: string; email?: string }): string {
    const source = (attendee.displayName || attendee.email || "?").trim();
    const words = source.split(/[\s.@_-]+/).filter(Boolean);
    return ((words[0]?.charAt(0) || "?") + (words.length > 1 ? words[1].charAt(0) : "")).toUpperCase();
  }

  get whenLabel(): string {
    if (!this.visit) return "";
    const zone = this.visit.timeZone || "UTC";
    const start = new Date(this.visit.startUtc);
    const end = new Date(this.visit.endUtc);
    // An incomplete record would make Intl throw "Invalid time value" on every change detection.
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return "";
    const day = formatInZone(start, zone, { weekday: "long", month: "long", day: "numeric", year: "numeric" });
    const from = formatInZone(start, zone, { hour: "numeric", minute: "2-digit" });
    const to = formatInZone(end, zone, { hour: "numeric", minute: "2-digit" });
    const label = VISIT_TIME_ZONES.find((z) => z.id === zone)?.label || zone;
    return `${day}, ${from} - ${to} (${label})`;
  }

  join(): void {
    if (this.visit?.joinUrl) window.open(this.visit.joinUrl, "_blank", "noopener");
  }

  loadTranscript(): void {
    if (!this.visit?.meetingCode || this.transcriptLoading) return;
    this.transcriptLoading = true;
    this.errorMessage = "";
    this.apiService.listMeetingTranscript(this.visit.meetingCode).subscribe({
      next: (lines) => {
        this.transcript = Array.isArray(lines) ? lines : [];
        this.transcriptLoading = false;
      },
      error: (err) => {
        this.transcriptLoading = false;
        this.errorMessage = apiErrorMessage(err, "The transcript could not be loaded.");
      },
    });
  }

  copyLink(): void {
    if (!this.visit?.joinUrl || !navigator.clipboard) return;
    // The Clipboard API's native promise settles outside Angular's zone; re-enter it to redraw.
    navigator.clipboard.writeText(this.visit.joinUrl).then(() =>
      this.ngZone.run(() => {
        this.copied = true;
        setTimeout(() => (this.copied = false), 2000);
      })
    );
  }

  edit(): void {
    this.dialogRef.close({ action: "edit", visit: this.visit });
  }

  resend(): void {
    this.run(this.apiService.resendVisit(this.visit.visitKey), (res) => {
      this.infoMessage =
        res?.invitationsFailed > 0
          ? `${res.invitationsSent} invitation(s) sent, ${res.invitationsFailed} could not be delivered. ${res.invitationError || ""}`.trim()
          : `Invitations re-sent to ${res?.invitationsSent ?? 0} recipient(s).`;
    });
  }

  confirmCancel(): void {
    this.confirmingCancel = true;
  }

  cancelVisit(): void {
    this.run(this.apiService.cancelVisit(this.visit.visitKey), () => {
      this.confirmingCancel = false;
      this.infoMessage = "Visit cancelled. Attendees have been sent a cancellation.";
    });
  }

  close(): void {
    this.dialogRef.close(this.changed ? { action: "changed" } : undefined);
  }

  private run(request: any, onDone: (res: any) => void): void {
    this.busy = true;
    this.errorMessage = "";
    this.infoMessage = "";
    request.subscribe({
      next: (res: any) => {
        this.busy = false;
        this.changed = true;
        if (res?.visitKey) this.visit = res;
        onDone(res);
      },
      error: (err: any) => {
        this.busy = false;
        this.errorMessage = apiErrorMessage(err, "The request could not be completed.");
      },
    });
  }
}
